import * as preact from "preact";
import { useState } from "preact/hooks";

export type ReaderFooterState = {
  disableMarginButtons?: boolean;
  hidden?: boolean;
  isEndOfText?: boolean;
  isLastPage?: boolean;
  isRTL?: boolean;
  location?: number;
  marginButtonOffset?: number;
  marginButtonsHidden?: boolean;
  nextActivated?: boolean;
  percentage?: number | string;
  previousActivated?: boolean;
  readAloudAvailable?: boolean;
  readAloudBuffering?: boolean;
  readAloudPlaying?: boolean;
  readAloudSkipButtonsVisible?: boolean;
  readAloudVisible?: boolean;
  totalLocations?: number;
};

const formatLocation = (location?: number) =>
  location === undefined ? "" : location.toLocaleString("en-US");

const ReadAloudButtonIcon = ({
  buffering,
  playing,
}: {
  buffering?: boolean;
  playing?: boolean;
}) => {
  if (buffering) {
    return <img class="throbber" src="/content/throbberBlack.gif" />;
  }
  return (
    <svg
      class="icon"
      width="148"
      height="148"
      viewBox="0 0 148 148"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {playing ? (
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M148 74C148 114.869 114.869 148 74 148C33.1309 148 0 114.869 0 74C0 33.1309 33.1309 0 74 0C114.869 0 148 33.1309 148 74ZM88 109C85.7909 109 84 107.209 84 105V42C84 39.7909 85.7909 38 88 38H101C103.209 38 105 39.7909 105 42V105C105 107.209 103.209 109 101 109H88ZM42 105C42 107.209 43.7909 109 46 109H59C61.2091 109 63 107.209 63 105V42C63 39.7909 61.2091 38 59 38H46C43.7909 38 42 39.7909 42 42V105Z"
          fill="currentColor"
        />
      ) : (
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M74 148C114.869 148 148 114.869 148 74C148 33.1309 114.869 0 74 0C33.1309 0 0 33.1309 0 74C0 114.869 33.1309 148 74 148ZM55 113.837L118 77.4641C120.667 75.9245 120.667 72.0755 118 70.5359L55 34.1628C52.3335 32.6232 49 34.5477 49 37.6269V110.373C49 113.452 52.3335 115.377 55 113.837Z"
          fill="currentColor"
        />
      )}
    </svg>
  );
};

// Touch taps emulate mouseenter without a matching mouseleave, which would leave
// the page-turn button stuck in its hover state, so only track real mice.
const mouseHoverHandlers = (setHovered: (hovered: boolean) => void) => ({
  onPointerEnter: (event: PointerEvent) =>
    setHovered(event.pointerType === "mouse"),
  onPointerLeave: () => setHovered(false),
});

const ReaderFooterView = ({
  footerState = {},
  onNextPage,
  onPageNavigationFurthest,
  onPageNavigationSpecific,
  onPageNavigationStart,
  onPageNumberClick,
  onPreviousPage,
  onReadAloudSkipBackward,
  onReadAloudSkipForward,
  onReadAloudToggle,
}: {
  footerState?: ReaderFooterState;
  onNextPage?: () => void;
  onPageNavigationFurthest?: () => void;
  onPageNavigationSpecific?: () => void;
  onPageNavigationStart?: () => void;
  onPageNumberClick?: () => void;
  onPreviousPage?: () => void;
  onReadAloudSkipBackward?: () => void;
  onReadAloudSkipForward?: () => void;
  onReadAloudToggle?: () => void;
}) => {
  const [nextHovered, setNextHovered] = useState(false);
  const [previousHovered, setPreviousHovered] = useState(false);
  const {
    disableMarginButtons,
    hidden,
    isEndOfText,
    isLastPage,
    isRTL,
    location,
    marginButtonOffset,
    marginButtonsHidden,
    nextActivated,
    percentage,
    previousActivated,
    readAloudAvailable,
    readAloudBuffering,
    readAloudPlaying,
    readAloudSkipButtonsVisible,
    readAloudVisible,
    totalLocations,
  } = footerState;

  const showContinue = !isEndOfText && isLastPage;
  const showReadAloud = Boolean(readAloudAvailable && readAloudVisible);
  const pageNumber =
    percentage === undefined || percentage === null
      ? "\u00a0"
      : `${percentage}%`;

  const previousPageTitle = isRTL ? "Next Page" : "Previous Page";
  const nextPageTitle = isRTL ? "Previous Page" : "Next Page";
  const nextHoverClass = nextHovered ? "hover" : "";
  const previousHoverClass = previousHovered ? "hover" : "";
  const goForward = () => {
    if (isRTL) {
      onPreviousPage?.();
    } else {
      onNextPage?.();
    }
  };
  const goBack = () => {
    if (isRTL) {
      onNextPage?.();
    } else {
      onPreviousPage?.();
    }
  };

  return (
    <>
      <div id="footer" style={{ display: hidden ? "none" : undefined }}>
        <span class="pageControls">
          <button
            class={`continueButton ${previousHoverClass}`}
            title="Continue"
            id="continueButtonRTL"
            onClick={goBack}
            style={{ display: showContinue && isRTL ? "block" : undefined }}
          >
            Continue
          </button>
          <button
            class={`pageControlButton ${
              showContinue && isRTL ? "lastPage" : ""
            } ${previousHoverClass}`}
            title={previousPageTitle}
            id="prevPageButton"
            onClick={goBack}
            style={{
              visibility: showContinue && isRTL ? "hidden" : undefined,
            }}
          >
            <i class="icon-caret-left icon-2x"></i>
          </button>

          <span class="spacer"></span>

          <span
            class="pageNumberContainer"
            onClick={() => onPageNumberClick?.()}
          >
            <span id="pageNumber">{pageNumber}</span>
            <div class="pageNavigationTooltip tooltip arrowBottom">
              <h4>
                Location <span id="location">{formatLocation(location)}</span> /{" "}
                <span id="totalLocations">
                  {formatLocation(totalLocations)}
                </span>
              </h4>
              <ul class="pageNavigationOptions">
                <li
                  id="pageNavigationStart"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onPageNavigationStart?.();
                  }}
                >
                  Go to start
                </li>
                <li
                  id="pageNavigationFurthest"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onPageNavigationFurthest?.();
                  }}
                >
                  Go to furthest read location
                </li>
                <li
                  id="pageNavigationSpecific"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onPageNavigationSpecific?.();
                  }}
                >
                  Go to specific location...
                </li>
              </ul>
            </div>
          </span>

          <span
            class="spacer readAloudSpacer"
            style={{ display: showReadAloud ? undefined : "none" }}
          ></span>
          <button
            class="skipBackward"
            onClick={() => onReadAloudSkipBackward?.()}
            style={{
              display:
                showReadAloud && readAloudSkipButtonsVisible
                  ? "inline-block"
                  : "none",
            }}
            title="Skip Backward"
          >
            <i class="icon-rotate-left"></i>
          </button>
          <span
            class="readAloudButtonContainer"
            style={{ display: showReadAloud ? "inline-block" : "none" }}
          >
            <button
              id="readAloudButton"
              class={readAloudBuffering ? "buffering" : undefined}
              onClick={() => onReadAloudToggle?.()}
            >
              <ReadAloudButtonIcon
                buffering={readAloudBuffering}
                playing={readAloudPlaying}
              />
            </button>
            <div class="playAudioTooltip tooltip arrowBottom">
              <p>Click here to play the attached audio file</p>
            </div>
          </span>
          <button
            class="skipForward"
            onClick={() => onReadAloudSkipForward?.()}
            style={{
              display:
                showReadAloud && readAloudSkipButtonsVisible
                  ? "inline-block"
                  : "none",
            }}
            title="Skip Forward"
          >
            <i class="icon-rotate-right"></i>
          </button>

          <span class="spacer"></span>

          <button
            class={`pageControlButton ${
              showContinue && !isRTL ? "lastPage" : ""
            } ${nextHoverClass}`}
            title={nextPageTitle}
            id="nextPageButton"
            onClick={goForward}
            style={{
              opacity: isEndOfText ? 0 : undefined,
              visibility: showContinue && !isRTL ? "hidden" : undefined,
            }}
          >
            <i class="icon-caret-right icon-2x"></i>
          </button>
          <button
            class={`continueButton ${nextHoverClass}`}
            title="Continue"
            id="continueButton"
            onClick={goForward}
            style={{ display: showContinue && !isRTL ? "block" : undefined }}
          >
            Continue
          </button>
        </span>
      </div>

      <div
        id="next"
        class={nextActivated ? "activated" : ""}
        onClick={goForward}
        {...mouseHoverHandlers(setNextHovered)}
        title={isRTL ? "Previous page" : "Next page"}
        style={{
          bottom: disableMarginButtons ? "0" : undefined,
          display: isEndOfText || marginButtonsHidden ? "none" : undefined,
          height: disableMarginButtons ? "80px" : undefined,
          right:
            marginButtonOffset !== undefined
              ? `${marginButtonOffset}px`
              : undefined,
          top: disableMarginButtons ? "auto" : undefined,
        }}
      >
        <div class="arrowRight"></div>
      </div>
      <div
        id="prev"
        class={previousActivated ? "activated" : ""}
        onClick={goBack}
        {...mouseHoverHandlers(setPreviousHovered)}
        title={isRTL ? "Next page" : "Previous page"}
        style={{
          bottom: disableMarginButtons ? "0" : undefined,
          display: marginButtonsHidden ? "none" : undefined,
          height: disableMarginButtons ? "80px" : undefined,
          left:
            marginButtonOffset !== undefined
              ? `${marginButtonOffset}px`
              : undefined,
          top: disableMarginButtons ? "auto" : undefined,
        }}
      >
        <div class="arrowLeft"></div>
      </div>
    </>
  );
};

export default ReaderFooterView;
